import instance, { getUri } from '../index';

const RESOURCE_SHOOT = 'shoot';
const RESOURCE_ACTION = 'action';

export const ContentApi = {
  downloadRaw({ id }) {
    const uri = getUri({ url: `${RESOURCE_SHOOT}/${id}/${RESOURCE_ACTION}/download-raw` });
    window.open(uri, 'Download');
  },

  /**
   * Upload Operations
   */
  uploadBrief({ id, blob }) {
    return instance.post(`${RESOURCE_SHOOT}/${id}/${RESOURCE_ACTION}/upload-brief`, blob, {
      headers: {
        'content-type': 'application/octet-stream'
      }
    });
  },
  /**
   * Download Operations
   */
  downloadBrief({ id }) {
    const uri = getUri({ url: `${RESOURCE_SHOOT}/${id}/${RESOURCE_ACTION}/download-brief` });
    window.open(uri, 'Download');
  },

  uploadRawsPreview({ id, blob, onUploadProgress }) {
    return instance.post(`${RESOURCE_SHOOT}/${id}/${RESOURCE_ACTION}/upload-raws-preview`, blob, {
      headers: {
        'Content-Type': 'application/octet-stream'
      },
      onUploadProgress
    });
  },

  /**
   * Multipart Upload Operations
   */
  initializeMultipartUpload({ id }) {
    return instance.post(`${RESOURCE_SHOOT}/${id}/${RESOURCE_ACTION}/init-multipart-upload`);
  },

  getUploadPartUrl({ id, uploadId, partNumber, key }) {
    return instance.get(`${RESOURCE_SHOOT}/${id}/${RESOURCE_ACTION}/upload-part-url`, {
      params: { uploadId, partNumber, key }
    });
  },

  completeMultipartUpload({ id, uploadId, key, parts }) {
    return instance.post(`${RESOURCE_SHOOT}/${id}/${RESOURCE_ACTION}/complete-multipart-upload`, { uploadId, key, parts });
  },

  abortMultipartUpload({ id, uploadId, key }) {
    return instance.post(`${RESOURCE_SHOOT}/${id}/${RESOURCE_ACTION}/abort-multipart-upload`, { uploadId, key });
  },

  /**
   * Presigned URL Operations
   */
  getPresignedUrls({ id, files }) {
    return instance.post(`${RESOURCE_SHOOT}/${id}/${RESOURCE_ACTION}/get-presigned-urls`, { files });
  },

  /**
   * Image and Video Operations
   */
  addContent({ id, payload }) {
    return instance.post(`${RESOURCE_SHOOT}/${id}/${RESOURCE_ACTION}/add-content`, payload);
  },

  createVideosBatch({ id, payload }) {
    return instance.post(`${RESOURCE_SHOOT}/${id}/${RESOURCE_ACTION}/add-content`, payload);
  },

  updateImageStatus({ id, payload }) {
    return instance.post(`/image/${id}/action/update-status`, payload);
  },

  updateRawImageStatus({ id, payload }) {
    return instance.post(`/raw/${id}/action/update-status`, payload);
  },

  updateImageMetadata({ content, id, payload }) {
    return instance.post(`/${content}/${id}/action/update-metadata`, payload);
  },

  bulkApproveImages({ payload }) {
    return instance.post('/image/action/bulk-approve', payload);
  },

  approveRemainingImages({ payload }) {
    return instance.post('/image/action/approve-remaining', payload);
  },

  bulkSelectRaws({ payload }) {
    return instance.post('/raw/action/bulk-select', payload);
  },

  updatePreview({ id, payload }) {
    return instance.post(`/shoot/${id}/action/update-preview`, payload);
  },

  updateRawsPreview({ id, payload }) {
    return instance.post(`/shoot/${id}/action/update-raws-preview`, payload);
  },

  deleteImage({ content, id }) {
    return instance.delete(`/${content}/${id}/action/delete`);
  },

  /**
   * Image Search and Format Operations
   */
  getContent({ filters = {}, pagination = { limit: 20, cursor: null } } = {}) {
    return instance
      .post('/content/search', {
        filters,
        pagination
      })
      .then(response => ({
        content: response.data.content,
        pagination: response.data.pagination
      }));
  },

  exportImages({ imageIds, formatOptions }) {
    return instance
      .post('/images/format', {
        imageIds,
        formatOptions: {
          width: formatOptions.width,
          height: formatOptions.height,
          fit: formatOptions.fit,
          format: formatOptions.format,
          quality: formatOptions.quality,
          preserveAspectRatio: formatOptions.preserveAspectRatio,
          watermark: formatOptions.watermark
        }
      })
      .then(response => response.data.images);
  },

  /**
   * Download Job Operations
   */
  enqueueDownloadJob({ id }) {
    return instance.post(`/${RESOURCE_SHOOT}/${id}/${RESOURCE_ACTION}/enqueue-download-job`).then(response => {
      return {
        shootId: response.data?.shootId,
        jobId: response.data?.jobId,
        status: response.data?.status || 'pending'
      };
    });
  },

  getDownloadJobStatus({ id }) {
    return instance.get(`/${RESOURCE_SHOOT}/${id}/${RESOURCE_ACTION}/enqueue-download-job/status`).then(response => {
      return {
        status: response.data?.status || 'pending',
        lastUpdated: new Date(response.data?.lastUpdated),
        progress: response.data?.progress,
        message: response.data?.message,
        startedAt: response.data?.startedAt ? new Date(response.data?.startedAt) : null,
        shootId: response.data?.shootId,
        downloadUrl: response.data?.downloadUrl || null
      };
    });
  },

  /**
   * Social Content Operations
   */
  generateHashtags({ caption }) {
    return instance.post('/social-content/generate-hashtags', { caption }).then(response => response.data.hashtags);
  },

  improveCaption({ caption }) {
    return instance.post('/social-content/improve-caption', { caption }).then(response => response.data.improvedCaption);
  },

  getSignedCookie() {
    return instance.post('/contents/signed-cookies', { url: 'https://cdn.app.flashy-staging.ae/323/e72c8aa4-dae3-495b-bd1f-54932950c851/hls/stream.m3u8' }).then(response => {
      return response.data;
    });
  },

  getVideos() {
    return instance.get('/videos').then(response => {
      return response.data;
    });
  },

  /**
   * Check processing status for multiple videos
   * @param {Array<number>} videoIds - Array of video IDs to check status
   * @returns {Promise} Promise containing the processing status for each video
   */
  getVideosProcessingStatus(videoIds) {
    return instance.post('/videos/processing-status', { videoIds });
  },

  /**
   * Check processing status for multiple content items (images and videos)
   * @param {Array<number>} contentIds - Array of content IDs to check status
   * @param {string} type - Content type ('image' or 'video')
   * @returns {Promise} Promise containing the processing status for each content item
   */
  getContentProcessingStatus({ contentIds, type }) {
    return instance.post('/content/processing-status', { contentIds, type });
  },

  /**
   * Add a label to an image
   * @param {string} id - The ID of the image
   * @param {string} label - The label to add
   * @returns {Promise<Array<string>>} Updated labels array
   */
  addLabelToImage({ id, label }) {
    return instance.post(`/content/image/${id}/labels`, { label }).then(response => response.data.labels);
  },

  /**
   * Remove a label from an image
   * @param {string} id - The ID of the image
   * @param {string} label - The label to remove
   * @returns {Promise<Array<string>>} Updated labels array
   */
  removeLabelFromImage({ id, label }) {
    return instance.delete(`/content/image/${id}/labels`, { data: { label } }).then(response => response.data.labels);
  },

  /**
   * Add a comment to an image
   * @param {string} id - The ID of the image
   * @param {string} content - The comment content
   * @returns {Promise<Object>} The created comment object
   */
  addCommentToImage({ id, content }) {
    return instance.post(`/content/image/${id}/comments`, { content }).then(response => response.data);
  },

  getImageComments({ id }) {
    // Ensure 'id' is a string or number for URL construction
    if (typeof id === 'undefined' || id === null || String(id).trim() === '') {
      return Promise.reject(new Error('Image ID is required to fetch comments.'));
    }
    return instance
      .get(`/content/image/${id}/comments`)
      .then(response => {
        // Ensure response.data and response.data.comments exist
        if (response && response.data && Array.isArray(response.data.comments)) {
          return response.data.comments;
        }
        // Return empty array or throw error if data is not as expected
        console.warn(`Comments data for image ${id} not in expected format:`, response.data);
        return [];
      })
      .catch(error => {
        console.error(`Failed to fetch comments for image ${id}:`, error);
        throw error; // Re-throw to be caught by TanStack Query's onError
      });
  },

  /**
   * Unified Content Comment Operations (Content-Agnostic)
   */

  /**
   * Add a comment to any content (image or video)
   * @param {string} type - Content type ('image' or 'video')
   * @param {string} id - The ID of the content
   * @param {string} content - The comment content
   * @returns {Promise<Object>} The created comment object
   */
  addCommentToContent({ type, id, content }) {
    return instance.post(`/content/${type}/${id}/comments`, { content }).then(response => response.data);
  },

  /**
   * Get comments for any content (image or video)
   * @param {string} type - Content type ('image' or 'video')
   * @param {string} id - The ID of the content
   * @returns {Promise<Array>} Array of comments
   */
  getContentComments({ type, id }) {
    // Ensure 'id' is a string or number for URL construction
    if (typeof id === 'undefined' || id === null || String(id).trim() === '') {
      return Promise.reject(new Error(`${type} ID is required to fetch comments.`));
    }

    // Validate content type
    if (!type || !['image', 'video'].includes(type)) {
      return Promise.reject(new Error('Content type must be either "image" or "video".'));
    }

    return instance
      .get(`/content/${type}/${id}/comments`)
      .then(response => {
        // Ensure response.data and response.data.comments exist
        if (response && response.data && Array.isArray(response.data.comments)) {
          return response.data.comments;
        }
        // Return empty array or throw error if data is not as expected
        console.warn(`Comments data for ${type} ${id} not in expected format:`, response.data);
        return [];
      })
      .catch(error => {
        console.error(`Failed to fetch comments for ${type} ${id}:`, error);
        throw error; // Re-throw to be caught by TanStack Query's onError
      });
  },

  /**
   * Unified Content Label Operations (Content-Agnostic)
   */

  /**
   * Add a label to any content (image or video)
   * @param {string} type - Content type ('image' or 'video')
   * @param {string} id - The ID of the content
   * @param {string} label - The label to add
   * @returns {Promise<Array<string>>} Updated labels array
   */
  addLabelToContent({ type, id, label }) {
    // Validate content type
    if (!type || !['image', 'video'].includes(type)) {
      return Promise.reject(new Error('Content type must be either "image" or "video".'));
    }

    // Ensure 'id' is valid
    if (typeof id === 'undefined' || id === null || String(id).trim() === '') {
      return Promise.reject(new Error(`${type} ID is required to add label.`));
    }

    return instance.post(`/content/${type}/${id}/labels`, { label }).then(response => response.data.labels);
  },

  /**
   * Remove a label from any content (image or video)
   * @param {string} type - Content type ('image' or 'video')
   * @param {string} id - The ID of the content
   * @param {string} label - The label to remove
   * @returns {Promise<Array<string>>} Updated labels array
   */
  removeLabelFromContent({ type, id, label }) {
    // Validate content type
    if (!type || !['image', 'video'].includes(type)) {
      return Promise.reject(new Error('Content type must be either "image" or "video".'));
    }

    // Ensure 'id' is valid
    if (typeof id === 'undefined' || id === null || String(id).trim() === '') {
      return Promise.reject(new Error(`${type} ID is required to remove label.`));
    }

    return instance.delete(`/content/${type}/${id}/labels`, { data: { label } }).then(response => response.data.labels);
  },

  /**
   * Convenience wrapper functions for unified operations
   * These automatically determine content type and provide a simpler interface
   */

  /**
   * Add a comment to content with automatic type detection
   * @param {string} id - The ID of the content
   * @param {string} content - The comment content
   * @param {string} contentType - Content type ('image' or 'video')
   * @returns {Promise<Object>} The created comment object
   */
  addComment({ id, content, contentType }) {
    return this.addCommentToContent({ type: contentType, id, content });
  },

  /**
   * Get comments for content with automatic type detection
   * @param {string} id - The ID of the content
   * @param {string} contentType - Content type ('image' or 'video')
   * @returns {Promise<Array>} Array of comments
   */
  getComments({ id, contentType }) {
    return this.getContentComments({ type: contentType, id });
  },

  /**
   * Add a label to content with automatic type detection
   * @param {string} id - The ID of the content
   * @param {string} label - The label to add
   * @param {string} contentType - Content type ('image' or 'video')
   * @returns {Promise<Array<string>>} Updated labels array
   */
  addLabel({ id, label, contentType }) {
    return this.addLabelToContent({ type: contentType, id, label });
  },

  /**
   * Remove a label from content with automatic type detection
   * @param {string} id - The ID of the content
   * @param {string} label - The label to remove
   * @param {string} contentType - Content type ('image' or 'video')
   * @returns {Promise<Array<string>>} Updated labels array
   */
  removeLabel({ id, label, contentType }) {
    return this.removeLabelFromContent({ type: contentType, id, label });
  },

  /**
   * Version Control Operations (Legacy - Image-specific methods)
   */
  addImageToStack({ id, stackId, versionName, versionComment, versionContentId }) {
    return instance.post(`/content/image/${id}/stack`, {
      stackId,
      versionName,
      versionComment,
      versionContentId
    });
  },

  removeImageFromStack({ id }) {
    return instance.delete(`/content/image/${id}/stack`);
  },

  restoreImageFromStack({ id }) {
    return instance.post(`/content/image/${id}/stack/restore`, {});
  },

  getContentsByStackId({ stackId, contentType = 'image' }) {
    return instance.get(`/content/stacks/${contentType}/${stackId}`);
  },

  createStack({ description }) {
    return instance.post('/stacks', { description });
  },

  /**
   * Unified Version Control Operations (Content-Agnostic)
   * These methods work with any content type (image, video, etc.)
   */

  /**
   * Get all content in a stack by stack ID (unified method)
   * @param {string} stackId - The stack ID
   * @param {string} contentType - Content type ('image' or 'video') - defaults to 'image'
   * @returns {Promise<Object>} Response containing all content in the stack
   */
  getContentByStackId({ stackId, contentType = 'image' }) {
    return instance.get(`/content/stacks/${contentType}/${stackId}`);
  },

  /**
   * Add content to a stack
   * @param {string} type - Content type ('image' or 'video')
   * @param {string} id - The ID of the content
   * @param {string} stackId - The stack ID to add content to
   * @param {string} versionName - Name for the version
   * @param {string} versionComment - Comment for the version
   * @param {string} versionContentId - ID of the content being added as version
   * @returns {Promise<Object>} Response from adding content to stack
   */
  addContentToStack({ type, id, stackId, versionName, versionComment, versionContentId }) {
    // check if type is 'image/jpeg' or any type of images
    // Validate content type
    if (!type) {
      type = 'image'; // Default to 'image' if not provided
    }

    if (type.startsWith('image/')) {
      type = 'image'; // Normalize to 'image' for any image type
    }

    if (!['image', 'video'].includes(type)) {
      return Promise.reject(new Error('Content type must be either "image" or "video".'));
    }

    return instance.post(`/content/${type}/${id}/stack`, {
      stackId,
      versionName,
      versionComment,
      versionContentId
    });
  },

  /**
   * Remove content from a stack
   * @param {string} type - Content type ('image' or 'video')
   * @param {string} id - The ID of the content to remove
   * @returns {Promise<Object>} Response from removing content from stack
   */
  removeContentFromStack({ type, id }) {
    // Validate content type
    if (!type || !['image', 'video'].includes(type)) {
      return Promise.reject(new Error('Content type must be either "image" or "video".'));
    }

    return instance.delete(`/content/${type}/${id}/stack`);
  },

  /**
   * Restore a content version from stack
   * @param {string} type - Content type ('image' or 'video')
   * @param {string} id - The ID of the content version to restore
   * @returns {Promise<Object>} Response from restoring content version
   */
  restoreContentVersion({ type, id }) {
    // Validate content type
    if (!type || !['image', 'video'].includes(type)) {
      return Promise.reject(new Error('Content type must be either "image" or "video".'));
    }

    return instance.post(`/content/${type}/${id}/stack/restore`, {});
  }
};
